/**
 * Block Extractor
 * Finds translatable block elements in DOM and parses them into ContentUnit.
 * Mirrors WordPress TS BlockExtractor and edge proxy block-level approach.
 */

import type { ContentUnit } from '../content/types.js';
import { ExcludeFilter, ExcludePatterns } from './exclude-filter.js';
import { parseBlockElement } from './markup-parser.js';
import { generateAutoKey } from './md5.js';

const BLOCK_SELECTOR =
  'p,h1,h2,h3,h4,h5,h6,li,td,th,label,button,figcaption,dt,dd,summary,caption';

// Inline elements eligible for "orphan" extraction when they have no block
// ancestor. Without this, markup like `<div><a>Transfer</a></div>` produces
// zero translatable units because <div>/<a> are absent from BLOCK_SELECTOR
// and the link has no <p>/<li>/etc. parent to absorb it as an indexed style.
// Conservative on purpose: only <a>. <span>/<button> can join later if real
// usage demands it (button is already a block tag).
const INLINE_BLOCK_SELECTOR = 'a';

// Tags to skip (same as PHP/edge)
const SKIP_TAGS = new Set([
  'SCRIPT',
  'STYLE',
  'CODE',
  'PRE',
  'TEXTAREA',
  'NOSCRIPT',
  'SVG',
]);

// Attributes that mark elements to skip
const SKIP_ATTRIBUTES = [
  'data-lionrapid-translated', // Server already translated
  'data-lionrapid-client', // Client already translated
  'data-no-translate', // Explicitly marked to skip
  // Edge proxy already rendered this block with the correct translation
  // and styles. Skip in normal extraction so the client-side auto-translator
  // can't overwrite the styled DOM with a stale (or plain-text) cache entry.
  // The editor passes includeTranslated=true to bypass this and re-extract
  // edge-rendered blocks for inspection.
  'data-lr-block',
];

export interface ExtractedBlock {
  element: Element;
  unit: ContentUnit;
  key: string;
}

export class BlockExtractor {
  private excludeFilter: ExcludeFilter;
  private excludeSelectors: string[];

  constructor(
    excludePatterns: Partial<ExcludePatterns> = {},
    excludeSelectors: string[] = [],
    minTextLength = 2
  ) {
    this.excludeFilter = new ExcludeFilter(excludePatterns, minTextLength);
    this.excludeSelectors = excludeSelectors;
  }

  /**
   * Extract all translatable block elements from a root.
   * @param includeTranslated - If true, include elements already marked as translated (for editor mode)
   */
  extract(root: Element | Document, includeTranslated = false): ExtractedBlock[] {
    const results: ExtractedBlock[] = [];
    const descendants = root.querySelectorAll(BLOCK_SELECTOR);

    // Include the root itself if it matches the block selector
    const elements: Element[] = [];
    if (root instanceof Element && root.matches(BLOCK_SELECTOR)) {
      elements.push(root);
    }
    for (let i = 0; i < descendants.length; i++) {
      elements.push(descendants[i]);
    }

    for (let i = 0; i < elements.length; i++) {
      const el = elements[i];

      // Skip if inside a SKIP_TAG ancestor
      if (this.isInsideSkipTag(el)) {
        continue;
      }

      // Skip if has skip attributes (unless editor mode includes translated)
      if (!includeTranslated && this.hasSkipAttribute(el)) {
        continue;
      }

      // Skip if matches exclude selectors
      if (this.matchesExcludeSelector(el)) {
        continue;
      }

      // Skip if has translate="no"
      if (el.getAttribute('translate') === 'no') {
        continue;
      }

      // Skip nested block elements (let the inner blocks be extracted)
      if (el.querySelector(BLOCK_SELECTOR)) {
        continue;
      }

      // For already-translated elements, use original HTML for key + unit
      const lrBlock = el.getAttribute('data-lr-block');
      const lrOriginal = el.getAttribute('data-lr-original');

      let unit: ContentUnit;
      if (lrOriginal) {
        // Parse original (source) HTML → correct ContentUnit for key generation
        const temp = document.createElement(el.tagName);
        temp.innerHTML = lrOriginal;
        unit = parseBlockElement(temp);
      } else {
        unit = parseBlockElement(el);
      }

      // Strip indexed tags for exclude filter
      const strippedText = unit.text.replace(/<\/?[0-9]+\/?>/g, '');

      // Apply exclude filter
      if (this.excludeFilter.shouldExclude(strippedText)) {
        continue;
      }

      // Key priority: data-lr-block (translated) → data-i18n (named) → auto-generate
      let key: string;
      if (lrBlock) {
        key = lrBlock;
      } else {
        const dataI18n = el.getAttribute('data-i18n');
        if (dataI18n) {
          key = dataI18n.includes(':') ? dataI18n : `common:${dataI18n}`;
        } else {
          key = generateAutoKey(unit.text);
        }
      }

      results.push({ element: el, unit, key });
    }

    this.extractOrphanInline(root, results, includeTranslated);

    return results;
  }

  /**
   * Second pass: extract inline elements (currently <a>) that have no block
   * ancestor. These would otherwise slip through both the block pass (not in
   * BLOCK_SELECTOR) and the inline-style capture (no parent block to host
   * them as `<1>...</1>`). Common shape: a `<div>` action group containing
   * only links.
   */
  private extractOrphanInline(
    root: Element | Document,
    results: ExtractedBlock[],
    includeTranslated: boolean
  ): void {
    const candidates = root.querySelectorAll(INLINE_BLOCK_SELECTOR);
    const elements: Element[] = [];
    if (root instanceof Element && root.matches(INLINE_BLOCK_SELECTOR)) {
      elements.push(root);
    }
    for (let i = 0; i < candidates.length; i++) {
      elements.push(candidates[i]);
    }

    for (let i = 0; i < elements.length; i++) {
      const el = elements[i];

      if (this.isInsideSkipTag(el)) continue;
      if (!includeTranslated && this.hasSkipAttribute(el)) continue;
      if (this.matchesExcludeSelector(el)) continue;
      if (el.getAttribute('translate') === 'no') continue;

      // Has a block ancestor — already captured as `<N>...</N>` by the
      // block pass (or its parent block was skipped, in which case we leave
      // it alone to avoid double-counting from a partially-skipped block).
      if (el.parentElement?.closest(BLOCK_SELECTOR)) continue;

      // Outer orphan wins: `<a><span>X</span></a>` → only the outer <a>.
      // (Currently INLINE_BLOCK_SELECTOR is just <a>, so this guards
      // against nested links in malformed HTML.)
      if (el.parentElement?.closest(INLINE_BLOCK_SELECTOR)) continue;

      // Contains a nested block — let the inner block be extracted instead.
      if (el.querySelector(BLOCK_SELECTOR)) continue;

      const lrOriginal = el.getAttribute('data-lr-original');
      let unit: ContentUnit;
      if (lrOriginal) {
        const temp = document.createElement(el.tagName);
        temp.innerHTML = lrOriginal;
        unit = parseBlockElement(temp);
      } else {
        unit = parseBlockElement(el);
      }

      const strippedText = unit.text.replace(/<\/?[0-9]+\/?>/g, '');
      if (this.excludeFilter.shouldExclude(strippedText)) continue;

      const lrBlock = el.getAttribute('data-lr-block');
      let key: string;
      if (lrBlock) {
        key = lrBlock;
      } else {
        const dataI18n = el.getAttribute('data-i18n');
        if (dataI18n) {
          key = dataI18n.includes(':') ? dataI18n : `common:${dataI18n}`;
        } else {
          key = generateAutoKey(unit.text);
        }
      }

      results.push({ element: el, unit, key });
    }
  }

  private isInsideSkipTag(element: Element): boolean {
    let current: Element | null = element.parentElement;
    while (current) {
      // Foreign elements (SVG/MathML) report a case-preserved tagName ("svg",
      // not "SVG"), so match case-insensitively or the SVG skip never fires.
      if (SKIP_TAGS.has(current.tagName.toUpperCase())) {
        return true;
      }
      current = current.parentElement;
    }
    return false;
  }

  private hasSkipAttribute(element: Element): boolean {
    let current: Element | null = element;
    while (current) {
      for (const attr of SKIP_ATTRIBUTES) {
        if (current.hasAttribute(attr)) {
          return true;
        }
      }
      current = current.parentElement;
    }
    return false;
  }

  private matchesExcludeSelector(element: Element): boolean {
    for (const selector of this.excludeSelectors) {
      try {
        if (element.matches(selector) || element.closest(selector)) {
          return true;
        }
      } catch {
        // Invalid selector, skip
      }
    }
    return false;
  }
}
